import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import NotificationBell from '../components/NotificationBell';
import BriefDeskLauncher from '../components/BriefDeskLauncher';
import {
  LayoutDashboard,
  Clock,
  Users,
  Briefcase,
  FileText,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  ChevronRight,
  Building2,
  ClipboardList,
  BarChart3
} from 'lucide-react';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter', 'Trainee'] },
  { path: '/time-tracking', icon: Clock, label: 'Time Tracking', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter', 'Trainee'] },
  { path: '/time-reports', icon: BarChart3, label: 'Time Reports', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter', 'Trainee'] },
  { path: '/teams', icon: Users, label: 'Teams', roles: ['Admin', 'Director', 'Account Manager'] },
  { path: '/roles', icon: Briefcase, label: 'Job Roles', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter', 'Trainee'] },
  { path: '/cvs', icon: FileText, label: 'CVs', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter', 'Trainee'] },
  { path: '/feedback', icon: MessageSquare, label: 'Feedback', roles: ['Admin', 'Director', 'Account Manager', 'Recruiter'] },
  { path: '/sops', icon: ClipboardList, label: 'SOPs', roles: ['Admin', 'Director'] },
  { path: '/users', icon: Building2, label: 'User Management', permission: 'view_users' },
];

export const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items based on roles or permissions
  const filteredNavItems = navItems.filter(item => {
    // If item has permission requirement, check permission
    if (item.permission) {
      return hasPermission(item.permission);
    }
    // Otherwise check roles
    return item.roles?.includes(user?.role);
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="font-semibold text-sm">HR Performance Hub</span>
        <div className="flex items-center gap-1">
          <BriefDeskLauncher compact />
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <nav className="fixed top-14 left-0 bottom-0 w-64 bg-card border-r p-4 animate-slide-in-left" onClick={e => e.stopPropagation()}>
            <ScrollArea className="h-full">
              <div className="space-y-1">
                {filteredNavItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                      location.pathname === item.path
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="space-y-1">
                <Link
                  to="/notifications"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    location.pathname === '/notifications'
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Bell className="h-4 w-4" />
                  Notifications
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    location.pathname === '/settings'
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </Link>
              </div>
            </ScrollArea>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex fixed top-0 left-0 bottom-0 flex-col bg-card border-r transition-all duration-300 z-30",
        sidebarOpen ? "w-64" : "w-16"
      )}>
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-4 border-b">
          {sidebarOpen && (
            <span className="font-bold text-sm tracking-tight">HR Performance Hub</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="h-8 w-8"
            data-testid="sidebar-toggle-btn"
          >
            <ChevronRight className={cn("h-4 w-4 transition-transform", sidebarOpen && "rotate-180")} />
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {filteredNavItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  location.pathname === item.path
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  !sidebarOpen && "justify-center"
                )}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            ))}
          </nav>
        </ScrollArea>

        {/* Bottom section */}
        <div className="border-t p-2 space-y-1">
          <Link
            to="/notifications"
            data-testid="nav-notifications"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
              location.pathname === '/notifications'
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              !sidebarOpen && "justify-center"
            )}
          >
            <Bell className="h-4 w-4" />
            {sidebarOpen && <span>Notifications</span>}
          </Link>
          <Link
            to="/settings"
            data-testid="nav-settings"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
              location.pathname === '/settings'
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              !sidebarOpen && "justify-center"
            )}
          >
            <Settings className="h-4 w-4" />
            {sidebarOpen && <span>Settings</span>}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className={cn("w-full", sidebarOpen ? "justify-start px-3 gap-3" : "justify-center")}
            data-testid="theme-toggle-desktop"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {sidebarOpen && <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
          </Button>
          <Separator className="my-2" />
          <div className={cn("flex items-center gap-3 px-3 py-2", !sidebarOpen && "justify-center")}>
            <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
              {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn("w-full text-destructive hover:text-destructive", sidebarOpen ? "justify-start px-3 gap-3" : "justify-center")}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-all duration-300 pt-14 lg:pt-0",
        sidebarOpen ? "lg:ml-64" : "lg:ml-16"
      )}>
        {/* Desktop Header Bar */}
        <header className="hidden lg:flex h-14 bg-card border-b items-center justify-end px-6 gap-4 sticky top-0 z-40">
          <BriefDeskLauncher />
          <NotificationBell />
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-desktop">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2 pl-4 border-l">
            <div className="text-right">
              <p className="text-sm font-medium">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground">{user?.role}</p>
            </div>
          </div>
        </header>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
